import { CheckCircle2, XCircle } from "lucide-react";
import { TABLE_ROWS, PLATFORMS } from "../../data/comparison";
import { AFFILIATE_URL, AFF_DISCLOSURE, LAST_UPDATED } from "../../lib/constants";
import { trackCta } from "../../lib/tracking";

const Cell = ({ value }) => {
  if (Array.isArray(value)) {
    const [flag, text] = value;
    return (
      <span className="inline-flex items-center gap-1.5">
        {flag === "yes" ? (
          <CheckCircle2 className="w-4 h-4 text-[#16A34A] shrink-0" />
        ) : (
          <XCircle className="w-4 h-4 text-slate-300 shrink-0" />
        )}
        <span className={flag === "yes" ? "" : "text-slate-500"}>{text}</span>
      </span>
    );
  }
  return <span>{value}</span>;
};

export const ComparisonTable = () => (
  <section id="comparison" className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-20" data-testid="comparison-table-section">
    <p className="text-xs font-bold uppercase tracking-[0.2em] text-slate-500 mb-3">Head-to-head data</p>
    <h2 className="font-heading text-3xl sm:text-4xl font-bold tracking-tight text-[#0F172A] mb-2">
      The 2026 Comparison Table
    </h2>
    <p className="text-sm text-slate-500 mb-4" data-testid="table-last-updated">Last Updated: {LAST_UPDATED}</p>
    <p className="text-base text-slate-700 leading-relaxed max-w-3xl mb-10">
      Here's how the five platforms compare across every metric that matters in 2026:
    </p>
    <div className="overflow-x-auto border border-slate-200 rounded-lg" data-testid="comparison-table-wrapper">
      <table className="w-full text-sm min-w-[860px]" data-testid="comparison-table">
        <thead>
          <tr className="border-b border-slate-200">
            <th className="sticky left-0 bg-[#F8FAFC] text-left p-4 font-bold text-xs uppercase tracking-wider text-slate-500 min-w-[150px]">Feature</th>
            {PLATFORMS.map((p, i) => (
              <th key={p} className={`text-left p-4 font-heading font-bold text-base text-[#0F172A] ${i === 0 ? "bg-[#5A31F4]/5 border-x border-[#5A31F4]/15" : "bg-white"}`}>
                {p}
                {i === 0 && (
                  <span className="block text-[10px] font-bold uppercase tracking-[0.15em] text-[#5A31F4] mt-1">Top Recommendation</span>
                )}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {TABLE_ROWS.map((row) => (
            <tr key={row.label} className="border-b border-slate-100 hover:bg-slate-50 transition-colors duration-200">
              <td className="sticky left-0 bg-[#F8FAFC] p-4 font-semibold text-slate-700">{row.label}</td>
              {row.values.map((v, i) => (
                <td key={i} className={`p-4 text-slate-700 ${i === 0 ? "bg-[#5A31F4]/5 border-x border-[#5A31F4]/15 font-semibold" : ""}`}>
                  <Cell value={v} />
                </td>
              ))}
            </tr>
          ))}
          <tr>
            <td className="sticky left-0 bg-[#F8FAFC] p-4"></td>
            <td className="p-4 bg-[#5A31F4]/5 border-x border-[#5A31F4]/15">
              <a
                href={AFFILIATE_URL}
                target="_blank"
                rel="nofollow sponsored noopener"
                onClick={() => trackCta("comparison-table", AFFILIATE_URL)}
                data-testid="cta-table-elevenlabs"
                className="inline-block bg-[#5A31F4] text-white hover:bg-[#4B29CD] rounded-md px-4 py-2.5 text-xs font-bold whitespace-nowrap transition-all duration-200 shadow-sm hover:-translate-y-0.5"
              >
                Get Started Free →
              </a>
              <span className="block text-[10px] text-slate-500 mt-2 max-w-[180px] leading-snug" data-testid="table-affiliate-disclosure">{AFF_DISCLOSURE}</span>
            </td>
            {[["https://murf.ai", "murf"], ["https://play.ht", "playht"], ["https://lovo.ai", "lovo"], ["https://www.resemble.ai", "resemble"]].map(([url, id]) => (
              <td key={id} className="p-4">
                <a href={url} target="_blank" rel="nofollow sponsored noopener" data-testid={`cta-table-${id}`} className="inline-block text-[#1A56DB] font-semibold text-xs hover:underline whitespace-nowrap">
                  Learn More →
                </a>
              </td>
            ))}
          </tr>
        </tbody>
      </table>
    </div>
    <p className="text-xs text-slate-500 mt-4 leading-relaxed" data-testid="table-editorial-note">
      Scores reflect VoiceRanker.ai's editorial evaluation based on factors including product features, pricing, usability, documentation, market reputation and editorial assessment. They should be considered comparative opinions rather than scientific measurements.
    </p>
    <p className="text-xs text-slate-500 mt-2 leading-relaxed" data-testid="table-pricing-note">
      * Prices were checked at the time of publication and may change. Always verify current pricing directly with the provider before purchasing.
    </p>
  </section>
);
